import PropTypes from 'prop-types';
import { SelectSearch } from 'mol-ui';
import isEqual from 'lodash/isEqual';
import { sharedActions } from 'core/Shared';
import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    useDispatch,
    useSelector
} from 'hooks';

const BasePicker = ({
    allowNone = false,
    async = false,
    changeUnique = false,
    defaultSearch = '',
    defaultValue,
    fetchAction,
    filter = () => true,
    hasError = false,
    height = '40px',
    listState,
    loadingState,
    margin = '0px 0px 10px 0px',
    maxMenuHeight,
    menuPlacement,
    nullPlaceholder,
    objectChange = false,
    onChange = () => null,
    params,
    reducer = 'option',
    value,
    width = '100%'
}) => {

    const dispatch = useDispatch();
    const { t } = useTranslation();

    const [defaultLoaded, setDefaultLoaded] = useState(false);
    const prevParamsRef = useRef(null);
    
    const option = useSelector(reducer);
    const list = useMemo(() => option[listState], [option, listState]);
    const loading = useMemo(() => option[loadingState], [option, loadingState]);

    useEffect(() => {
        if (!isEqual(prevParamsRef.current, params) && !async) {
            prevParamsRef.current = params;
            dispatch(fetchAction(params));
        }
    }, [async, params]);

    useEffect(() => {
        if (!changeUnique || list.length !== 1) return;
        onChange(objectChange ? list[0] : list[0]?.value);
    }, [changeUnique, list, objectChange]);

    const onInputChange = useCallback(value => {
        if (!value) return;
        dispatch(sharedActions.actionThrottle({
            action: fetchAction,
            args: {
                ...params,
                minLength: 3,
                search: value
            }
        }));
    }, [params]);

    useEffect(() => {
        if (!async || !defaultSearch || defaultLoaded) return;
        setDefaultLoaded(true);
        onInputChange(defaultSearch);
    }, [defaultSearch, onInputChange, setDefaultLoaded]);
    
    const handleChange = newValue => {
        if (!objectChange) {
            onChange(newValue);
        } else {
            onChange(list.find(({ value }) => value === newValue));
        }
    };

    useEffect(() => {
        if (!defaultValue) return;
        handleChange(defaultValue);
    }, [defaultValue, list]);

    const parsedNullPlaceholder = nullPlaceholder ?? t('Sin definir');
    const parsedPlaceholder = nullPlaceholder ?? t('Seleccione un elemento');

    const selectProps = {
        hasError,
        height,
        margin,
        maxMenuHeight,
        menuPlacement,
        value,
        width
    };

    return (
        <SelectSearch
            { ...selectProps }
            asyncLoad={ async }
            disabled={ !async && loading }
            fontSize="11px"
            loading={ loading }
            options={ [...(allowNone ? [{ label: parsedNullPlaceholder, value: 0 }] : []), ...(list ?? []).filter(filter)] }
            placeholder={ loading ?  `${ t('Cargando') }...` : parsedPlaceholder }
            onChange={ handleChange }
            onInputChange={ async ? onInputChange : undefined }
        />
    );
};

BasePicker.propTypes = {
    allowNone: PropTypes.bool,
    async: PropTypes.bool,
    changeUnique: PropTypes.bool,
    defaultSearch: PropTypes.string,
    defaultValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    fetchAction: PropTypes.func,
    filter: PropTypes.func,
    hasError: PropTypes.bool,
    height: PropTypes.string,
    listState: PropTypes.string,
    loadingState: PropTypes.string,
    margin: PropTypes.string,
    maxMenuHeight: PropTypes.number,
    menuPlacement: PropTypes.oneOf(['auto', 'bottom', 'top']),
    nullPlaceholder: PropTypes.string,
    objectChange: PropTypes.bool,
    onChange: PropTypes.func,
    params: PropTypes.any,
    reducer: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    width: PropTypes.string
};

export default BasePicker;
